import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import AppsIcon from '@atlaskit/icon/core/apps';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useOAuthHandlerFactory } from '@atlassian/conversation-assistant-chat-prompt-input/useOAuthHandlerFactory';
import type { ConnectorState } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useConnectorsDataReady } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';

import { useRovoSettingsConfig } from '../../../../common/utils/rovo-settings-config';
import { useAssistanceServiceParams } from '../../../../common/utils/use-assistance-service-params';
import { useRovoSettingsTapTraits } from '../../../../services/hooks/use-rovo-settings-tap-traits';

import { AppCard } from './app-card';
import { connectorDescriptionMessages } from './constants';
import { DiscoveryContentEmptyState } from './empty-state';
import { isFirstPartySource } from './first-party-sources';
import { DiscoveryContentLoadingState } from './loading-state';
import { messages } from './messages';

const styles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: token('space.200'),
		minWidth: '480px',
	},
	headingIconContainer: {
		display: 'flex',
		width: '40px',
		height: '40px',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
		flexShrink: 0,
	},
});

const SOCIAL_PROOF_THRESHOLD_PCT = 5;
const SOCIAL_PROOF_MAX_COUNT = 4;

/**
 * Ranks connectors according to the social proof visibility rules:
 *
 * 1. Connectors with connector auth % >= SOCIAL_PROOF_THRESHOLD_PCT are "social proof eligible".
 * 2. Social proof eligible connectors are sorted descending by auth %.
 * 3. At most SOCIAL_PROOF_MAX_COUNT social proof banners are shown, and the count is rounded
 *    down to the nearest even number (so banners always appear in pairs in the 2-column grid).
 * 4. The remaining connectors (below threshold, or beyond the even-pair cap) are sorted
 *    descending by smartlink exposure rate.
 * 5. The final order is: [social-proof group] ++ [remaining group].
 *
 * Returns both the ranked list and the set of sources that should display a social proof banner.
 */
const rankConnectors = (
	sources: ConnectorState[],
	connectorAuthStats: Record<string, number> | undefined,
	smartlinkExposureRate: Record<string, number> | undefined,
): { ranked: ConnectorState[]; socialProofSources: Set<string> } => {
	// Split into eligible (auth % >= threshold) and remaining.
	const eligible: ConnectorState[] = [];
	const remaining: ConnectorState[] = [];

	for (const source of sources) {
		const authPct = connectorAuthStats?.[source.source] ?? 0;
		if (authPct >= SOCIAL_PROOF_THRESHOLD_PCT) {
			eligible.push(source);
		} else {
			remaining.push(source);
		}
	}

	// Sort eligible by auth % descending.
	eligible.sort((a, b) => {
		const pctA = connectorAuthStats?.[a.source] ?? 0;
		const pctB = connectorAuthStats?.[b.source] ?? 0;
		return pctB - pctA;
	});

	// Cap to SOCIAL_PROOF_MAX_COUNT, rounded down to nearest even number.
	const rawCount = Math.min(eligible.length, SOCIAL_PROOF_MAX_COUNT);
	const socialProofCount = rawCount % 2 === 0 ? rawCount : rawCount - 1;

	const socialProofGroup = eligible.slice(0, socialProofCount);
	const overflowGroup = eligible.slice(socialProofCount);

	// Sort remaining (+ overflow) by smartlink exposure rate descending.
	const remainingAll = [...overflowGroup, ...remaining];
	remainingAll.sort((a, b) => {
		const rateA = smartlinkExposureRate?.[a.source] ?? -1;
		const rateB = smartlinkExposureRate?.[b.source] ?? -1;
		return rateB - rateA;
	});

	const socialProofSources = new Set(socialProofGroup.map((s) => s.source));

	return { ranked: [...socialProofGroup, ...remainingAll], socialProofSources };
};

export interface DiscoveryContentProps {
	/** Knowledge sources from the connectors store */
	knowledgeSources: ConnectorState[];
}

export const DiscoveryContent = ({
	knowledgeSources,
}: DiscoveryContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [searchQuery, setSearchQuery] = useState('');
	const { siteId, surface: modalSurface } = useRovoSettingsConfig();
	const createOAuthHandler = useOAuthHandlerFactory();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-settings-modal',
	});

	useEnsureConnectorsLoaded({ assistanceServiceParams });

	const [connectorsDataReady] = useConnectorsDataReady();

	// ASIMO-4082 will replace this with the final non-experiment-controlled logic. For now, we're hardcoding to true.
	const isSocialProofEnabled = true;

	const {
		loaded: tapTraitsLoaded,
		smartlinkExposureRate,
		connectorAuthStats,
	} = useRovoSettingsTapTraits(siteId);

	const thirdPartyKnowledgeSources = useMemo(
		() => knowledgeSources.filter((source) => !isFirstPartySource(source.source)),
		[knowledgeSources],
	);

	const { ranked: rankedSources, socialProofSources } = useMemo(
		() =>
			rankConnectors(
				thirdPartyKnowledgeSources,
				isSocialProofEnabled ? connectorAuthStats : undefined,
				smartlinkExposureRate,
			),
		[thirdPartyKnowledgeSources, isSocialProofEnabled, connectorAuthStats, smartlinkExposureRate],
	);

	const filteredSources = useMemo(() => {
		if (!searchQuery.trim()) {
			return rankedSources;
		}
		const query = searchQuery.toLowerCase();
		return rankedSources.filter((source) => source.friendlyName.toLowerCase().includes(query));
	}, [rankedSources, searchQuery]);

	const handleSearch = useCallback((searchText: string) => {
		setSearchQuery(searchText);
	}, []);

	const handleConnect = useCallback(
		(source: ConnectorState) => {
			const { onAuthorize } = createOAuthHandler({
				friendlyName: source.friendlyName,
				appSource: source.source,
				outboundAuthUrl: source.outboundAuthUrl,
				analyticsAttributes: {
					source: 'rovoSettingsModal',
					surface: modalSurface,
				},
			});
			onAuthorize();
		},
		[createOAuthHandler, modalSurface],
	);

	const getConnectorDescription = useCallback(
		(source: string): string | undefined => {
			const messageDescriptor =
				connectorDescriptionMessages[source as keyof typeof connectorDescriptionMessages];
			return messageDescriptor ? formatMessage(messageDescriptor) : undefined;
		},
		[formatMessage],
	);

	const getSocialProofPercentage = useCallback(
		(source: string): number | undefined => {
			if (!socialProofSources.has(source) || !connectorAuthStats) {
				return undefined;
			}
			return connectorAuthStats[source];
		},
		[socialProofSources, connectorAuthStats],
	);

	// Both TAP traits and the connectors store must have loaded before we can
	// decide between the app grid and the empty state.
	const dataReady = tapTraitsLoaded && connectorsDataReady;
	const showEmptyState = dataReady && thirdPartyKnowledgeSources.length === 0;
	const showHeader = dataReady && !showEmptyState;

	return (
		<Stack space="space.200">
			{/* Header with dark circular icon, title and description — only shown when apps are available */}
			{showHeader && (
				<>
					<Flex gap="space.150" alignItems="center">
						<Box xcss={styles.headingIconContainer}>
							<AppsIcon label="" color="currentColor" />
						</Box>
						<Stack>
							<Text weight="bold" size="large">
								{formatMessage(messages.connectAppsHeading)}
							</Text>
							<Text size="medium" color="color.text.subtle">
								{formatMessage(messages.connectAppsDescription)}
							</Text>
						</Stack>
					</Flex>

					<SearchInput
						isCompact
						variant="overlay"
						placeholderText={formatMessage(messages.searchAppsPlaceholder)}
						onSearch={handleSearch}
					/>
				</>
			)}

			{/* Wait for both tap traits and the connectors store before rendering apps to
			    avoid a visible reorder flash or a false empty state. */}
			{!dataReady ? (
				<DiscoveryContentLoadingState />
			) : showEmptyState ? (
				<DiscoveryContentEmptyState />
			) : (
				<Box xcss={styles.grid}>
					{filteredSources.map((source) => (
						<AppCard
							key={source.source}
							displayName={source.friendlyName}
							description={getConnectorDescription(source.source)}
							iconUrl={source.iconUrl}
							isConnected={source.isUserOAuthed}
							onConnect={() => handleConnect(source)}
							socialProofPercentage={getSocialProofPercentage(source.source)}
							testId={`discovery-app-card-${source.source}`}
						/>
					))}
				</Box>
			)}
		</Stack>
	);
};
